import './index.css';
import { jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useEscapeToClose, useKeyboardNavigation, findNextFocusableElement } from '@hh.ru/magritte-common-keyboard';
import { useCloseByClickOutside } from '@hh.ru/magritte-common-mouse';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useNoBubbling } from '@hh.ru/magritte-common-use-no-bubbling';
import { useWhenFontLoaded } from '@hh.ru/magritte-common-use-when-font-loaded';
import { useIntersectionSubscription } from './intersectionManager.js';
import { usePositionManager } from './positionManager.js';
import './findBestFitPlacement.js';
import './useMonitorResize.js';

var styles = {"drop-base":"magritte-drop-base___uJNG4_7-0-13","dropBase":"magritte-drop-base___uJNG4_7-0-13"};

const DropBaseComponent = ({ visible, children, arrowGuardDistance, activatorRef, preferredViewport, onClose, autoFocusWhenOpened = false, closeByClickOutside = true, animationTimeout, animationClassNames, guardDistanceToActivator = 8, guardDistanceToViewport = 24, minHeight = 200, role, widthEqualToActivator = false, allowShrinkHeightToFitIntoViewport = false, keyboardNavigationMode = null, ignoreIntersections = false, style = {}, onAppear, onBeforeExit, onAfterExit, host, placement, forceDropMetricsUpdate, lockPlacement = false, ...props }, ref) => {
    const dropNodeRef = useRef(null);
    const [renderPortal, setRenderPortal] = useState(false);
    const isVisibleDropDown = !!(renderPortal && visible);
    const [lockClickOutside, setLockClickOutside] = useState(true);
    useWhenFontLoaded(() => setRenderPortal(true));
    const { bestFitPlacementRect, resizeRefCallbackFactory } = usePositionManager(activatorRef, dropNodeRef, isVisibleDropDown, placement, preferredViewport, allowShrinkHeightToFitIntoViewport, widthEqualToActivator, arrowGuardDistance || {}, guardDistanceToActivator, guardDistanceToViewport, minHeight, forceDropMetricsUpdate, lockPlacement);
    useIntersectionSubscription(activatorRef, isVisibleDropDown, ignoreIntersections, () => {
        onClose?.();
    });
    const escapeToCloseRef = useEscapeToClose(onClose);
    const keyboardNavigationRef = useKeyboardNavigation(keyboardNavigationMode !== null
        ? { enabled: isVisibleDropDown, mode: keyboardNavigationMode }
        : { enabled: false });
    const dropNodeRefCallback = useMultipleRefs(ref, dropNodeRef, keyboardNavigationRef, escapeToCloseRef);
    useCloseByClickOutside(dropNodeRef, () => {
        if (isVisibleDropDown && !lockClickOutside && activatorRef.current !== document.activeElement) {
            onClose?.();
        }
    }, closeByClickOutside);
    const eventHandlers = useNoBubbling(props);
    useEffect(() => {
        if (!isVisibleDropDown) {
            setLockClickOutside(true);
        }
    }, [isVisibleDropDown, setLockClickOutside]);
    useEffect(() => {
        if (autoFocusWhenOpened && dropNodeRef.current !== null && isVisibleDropDown && !lockClickOutside) {
            const nextFocusableElement = findNextFocusableElement(dropNodeRef.current, null, 'down', 'list');
            if (nextFocusableElement !== null) {
                nextFocusableElement.focus();
            }
            else {
                dropNodeRef.current.focus();
            }
        }
    }, [autoFocusWhenOpened, isVisibleDropDown, lockClickOutside]);
    return (jsx(Fragment, { children: renderPortal &&
            createPortal(jsx(CSSTransition, { in: visible, onEntered: () => {
                    setLockClickOutside(false);
                    onAppear?.();
                }, onExited: onAfterExit, onExit: onBeforeExit, timeout: animationTimeout, classNames: animationClassNames, unmountOnExit: true, appear: true, nodeRef: dropNodeRef, children: jsx("div", { ...props, ...eventHandlers, "data-qa": "drop-base", tabIndex: autoFocusWhenOpened ? 0 : -1, className: styles.dropBase, ref: dropNodeRefCallback, style: {
                        ...style,
                        ...(bestFitPlacementRect || {}),
                    }, role: role, children: children(resizeRefCallbackFactory) }) }), host?.current ?? document.body) }));
};
const DropBase = forwardRef(DropBaseComponent);

export { DropBase };
//# sourceMappingURL=DropBase.js.map
